"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import ProductCard from "@/components/card/ProductCard";
import { ProductGridSkeleton } from "@/components/skeleton/ProductCardSkeleton";
import AsianBasketFooter from "@/components/footer/AsianBasketFooter";
import { isGroceryProduct, isTakeoutProduct } from "@/lib/orderType";
import "./weekly-specials.css";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function hasWeeklySpecialTag(product) {
  const tags = (product?.tags || []).map((tag) => normalize(tag?.value || tag));

  return tags.some(
    (tag) =>
      tag === "weekly-special" ||
      tag === "weekly special" ||
      tag.includes("weekly-special") ||
      tag.includes("weekly special"),
  );
}

function expandProductsByVariants(products = []) {
  return products.flatMap((product) => {
    const variants = product?.variants || [];

    if (!variants.length) {
      return [product];
    }

    return variants.map((variant) => ({
      ...product,
      variants: [variant],
      storefront_card_id: `${product.id}-${variant.id}`,
    }));
  });
}

export default function WeeklySpecialsPage() {
  const [tab, setTab] = useState("all");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadWeeklySpecials() {
      try {
        const response = await fetch("/api/weekly-specials", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) throw new Error("Could not load weekly specials.");
        const data = await response.json();
        if (!controller.signal.aborted) setProducts(data.products || []);
      } catch (error) {
        if (!controller.signal.aborted) setError(error.message);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    loadWeeklySpecials();
    return () => controller.abort();
  }, []);

  const specials = useMemo(() => {
    return products.filter(hasWeeklySpecialTag);
  }, [products]);

  const grocerySpecials = useMemo(() => {
    return expandProductsByVariants(specials.filter(isGroceryProduct));
  }, [specials]);

  const takeoutSpecials = useMemo(() => {
    return expandProductsByVariants(specials.filter(isTakeoutProduct));
  }, [specials]);

  const showGrocery = tab === "all" || tab === "grocery";
  const showTakeout = tab === "all" || tab === "takeout";

  return (
    <main className="ab-weekly-specials-page">
      <section className="ab-weekly-specials-hero">
        <div className="ab-weekly-specials-hero-inner">
          {/* <p className="ab-weekly-specials-eyebrow">Fresh deals every week</p> */}
          <h1>Weekly Specials</h1>
          <p>
            Great deals on your favourite groceries <br /> and delicious
            takeout.
          </p>

          <div className="ab-weekly-specials-tabs">
            <button
              type="button"
              className={tab === "all" ? "is-active" : ""}
              onClick={() => setTab("all")}
            >
              All Specials
            </button>
            <button
              type="button"
              className={tab === "grocery" ? "is-active" : ""}
              onClick={() => setTab("grocery")}
            >
              Grocery Deals
            </button>
            <button
              type="button"
              className={
                tab === "takeout" ? "is-active is-orange" : "is-orange"
              }
              onClick={() => setTab("takeout")}
            >
              Takeout Deals
            </button>
          </div>
        </div>
      </section>

      <div className="custom-container">
        {showGrocery && (
          <section className="ab-weekly-specials-section">
            <div className="ab-weekly-specials-section-head">
              <div className="ab-weekly-specail-heading-wrap">
                <span className="ab-weekly-specail-round-section-icon">
                  <img src="/assets/icons/basket.svg" alt="" />
                </span>
                <div className="ab-weekly-specail-heading">
                  <h2>Grocery Specials</h2>
                  <p>Fresh savings for your kitchen</p>
                </div>
              </div>

              <Link href="/shop/groceries">View All Grocery Deals →</Link>
            </div>

            {loading ? (
              <ProductGridSkeleton count={5} className="ab-weekly-specials-grid" />
            ) : error ? (
              <div className="ab-weekly-specials-empty" role="alert">{error}</div>
            ) : grocerySpecials.length ? (
              <div className="ab-weekly-specials-grid">
                {grocerySpecials.map((product) => (
                  <ProductCard
                    key={product.storefront_card_id || product.id}
                    product={product}
                  />
                ))}
              </div>
            ) : (
              <div className="ab-weekly-specials-empty">
                No grocery specials available right now.
              </div>
            )}
          </section>
        )}

        {(tab === "all" || (showGrocery && showTakeout)) && (
          <section className="ab-weekly-specials-mid-banner">
            <div className="ab-weekly-specials-stock-heading">
              <h3>Stock up for the week</h3>
              <p>
                Fresh ingredients, better meals, and bigger savings for your
                family.
              </p>
            </div>

            <div className="ab-weekly-specials-benefits">
              <div className="ab-weekly-specials-benefits-repeat">
                <img
                  src="/assets/icons/fresh-quality.png"
                  alt="fresh-quality"
                />
                <p>
                  <b>Fresh Quality</b> <br />
                  You Can Trust
                </p>
              </div>

              <div className="ab-weekly-specials-benefits-repeat">
                <img
                  src="/assets/icons/local-delivery.png"
                  alt="local-delivery"
                />
                <p>
                  <b> Local Delivery</b> <br /> In Fastivals & Event
                </p>
              </div>

              <div className="ab-weekly-specials-benefits-repeat">
                <img src="/assets/icons/dollar.png" alt="dollar" />

                <p>
                  <b> Great Price</b> <br />
                  Every Week{" "}
                </p>
              </div>
            </div>
          </section>
        )}

        {showTakeout && (
          <section className="ab-weekly-specials-section is-takeout">
            <div className="ab-weekly-specials-section-head">
              <div className="ab-weekly-specail-heading-wrap">
                <span className="ab-weekly-specail-round-section-icon">
                  <img src="/assets/icons/takeout.svg" alt="" />
                </span>
                <div className="ab-weekly-specail-heading">
                  <h2>Takeout Specials</h2>
                  <p>Your favourite dishes at special prices</p>
                </div>
              </div>

              <Link href="/shop/take-out">View All Takeout Deals →</Link>
            </div>

            {loading ? (
              <ProductGridSkeleton count={5} className="ab-weekly-specials-grid" />
            ) : error ? (
              <div className="ab-weekly-specials-empty" role="alert">{error}</div>
            ) : takeoutSpecials.length ? (
              <div className="ab-weekly-specials-grid">
                {takeoutSpecials.map((product) => (
                  <ProductCard
                    key={product.storefront_card_id || product.id}
                    product={product}
                  />
                ))}
              </div>
            ) : (
              <div className="ab-weekly-specials-empty">
                No takeout specials available right now.
              </div>
            )}
          </section>
        )}

        {!loading && !error && !specials.length && (
          <div className="ab-weekly-specials-empty big">
            No weekly special items were found. Add the tag{" "}
            <strong>weekly-special</strong> in Medusa to show products here.
          </div>
        )}

        {/* <section className="ab-weekly-specials-footer-strip">
          <div>Fresh Quality</div>
          <div>Serving Our Community</div>
          <div>Flexible Ordering</div>
          <div>Need Help?</div>
        </section> */}

        <AsianBasketFooter />
      </div>
    </main>
  );
}
